import regression from 'regression'
import { GadsFieldsRequired, Targets } from '..'
import { resources } from '../gads'

export interface ConversionData {
    [resource_name: string]: {
        current_bid: number
        performance_mode: Targets.PerformanceMode
        performance_target: number
        target_mode: Targets.TargetMode
        lookback_window: number
        clicks: number
        cost: number
        conversions: number
        conversions_value: number
        conversion_rate: number
        value_per_conversion: number
    }
}

export enum BiddingStrategy {
    Cpa = 'cpa',
    Roas = 'roas',
    Profit = 'profit',
}

export interface InvalidPrediction {
    invalid: boolean
    reason: string
}

export interface InvalidPredictionData extends InvalidPrediction {
    criterion_resource_name: string
}

export type PredictionPoints = [number, number]

export interface Regressions {
    clicks_regression: regression.Result
    cost_regression: regression.Result
}

export interface PredictionPointMetrics {
    cost: number
    clicks: number
    conversions?: number
    conversions_value?: number
    cpa?: number
    roas?: number
    profit?: number
}

export interface BidModifierPrediction {
    cpa_regression: regression.Result
    profit_regression: regression.Result
    roas_regression: regression.Result
    simulation_points: BidModifierSimulationPoint[]
}

export interface BidModifierSimulationPoint {
    bid_modifier: number
    cost: number
    clicks: number
    conversions: number
    cpa: number
    roas: number
    profit: number
}

export interface RawCampaignCriterionSimulation {
    campaign_criterion: {
        type: number
        resource_name: string
        bid_modifier: number
    }
    campaign_criterion_simulation: {
        bid_modifier_point_list: { points: BidModifierPointList[] }
    }
}

export interface BestBidModifier {
    current_bid_mod: number
    bid_modifier_data: BidModifierPoint[]
    best_bid_modifier: BidModifierPoint
    simulation_points: BidModifierSimulationPoint[]
    invalid?: boolean
    reason?: string
}

export interface BestBidModifierData extends BestBidModifier {
    conversion_rate: number
    criterion_resource_name: string
}

export interface BidModifierPointList {
    bid_modifier: number
    biddable_conversions: number
    biddable_conversions_value: number
    clicks: number
    cost_micros: number
    impressions: number
    top_slot_impressions: number
    parent_biddable_conversions: number
    parent_biddable_conversions_value: number
    parent_clicks: number
    parent_cost_micros: number
    parent_impressions: number
    parent_top_slot_impressions: number
    parent_required_budget_micros: number
}

export interface BidModifierPoint {
    bid_modifier: number
    cpa?: number
    roas?: number
    profit?: number
}

export interface BidGenerationContext {
    performance_target_settings: {
        performance_mode: Targets.PerformanceMode
        target_cpa?: number
        target_roas?: number
        target_cpa_set: boolean
        target_roas_set: boolean
    }
    environment: {
        cost: number
        all_conversions_value: number
        all_conversions: number
        force_cpa?: number
    }
    entity: {
        cost: number
        all_conversions_value: number
        all_conversions: number
        current_bid_mod: number
    }
    campaign_is_budget_capped: boolean
    bid_mod_percentage_difference_limit?: number
}

export interface BidGenerationReturn {
    new_bid_mod: number
    valid: boolean
    reason?: string
    environment: {
        cpa?: number
        roas?: number
        profit?: number
        roi?: number
    }
    entity: {
        cpa?: number
        roas?: number
        profit?: number
        roi?: number
    }
}

export namespace BidFactory {
    export const BidFactoryError = {
        NO_AD_GROUP_CRITERION_SIMULATION: 'no_ad_group_criterion_simulation',
        NO_CLICKS_IN_SIMULATION: 'no_clicks_in_simulation',
        NO_VALID_REGRESSIONS: 'no_valid_regressions',
        ROAS_WITH_NO_TARGET: 'roas_with_no_target',
        CPA_WITH_NO_TARGET: 'cpa_with_no_target',
        PROFIT_WITH_NO_MARGIN: 'profit_with_no_margin',
        NO_VALID_BIDDING_STRATEGY: 'no_valid_bidding_strategy',
        NO_BEST_CPC_BID: 'no_best_cpc_bid',
        BID_NOT_INCREASING_METRICS: 'bid_not_increasing_metrics',
        BID_NOT_DECREASING_METRICS: 'bid_not_decreasing_metrics',
        CURRENT_BID_NOT_IN_SIMULATION: 'current_bid_not_in_simulation',
        NO_POINT_CLOSE_TO_CPA: 'no_point_close_to_cpa',
        NO_POINT_CLOSE_TO_ROAS: 'no_point_close_to_roas',
        NO_VALID_SIMULATION_POINTS: 'no_valid_simulation_points',
        NO_CPC_BID_IN_SIMULATION: 'no_cpc_bid_in_simulation',
        NO_POINT_WITH_MAX_PROFIT: 'no_point_with_max_profit',
        NO_CRITERION_CONVERSION_RATE: 'no_criterion_conversion_rate',
    } as const

    export type Error = (typeof Error)[keyof typeof Error]

    export const ErrorReadable = {
        [BidFactoryError.NO_AD_GROUP_CRITERION_SIMULATION]:
            'No ad group criterion simulation available for this domain',
        [BidFactoryError.NO_CLICKS_IN_SIMULATION]: "The simulation doesn't have any clicks",
        [BidFactoryError.NO_VALID_REGRESSIONS]: 'No valid regressions could be generated',
        [BidFactoryError.ROAS_WITH_NO_TARGET]:
            'Using ROAS mode with no target or no value per conversion',
        [BidFactoryError.NO_BEST_CPC_BID]: 'No best CPC bid could be found',
        [BidFactoryError.BID_NOT_INCREASING_METRICS]:
            'Increasing a bid should increase cost and clicks',
        [BidFactoryError.BID_NOT_DECREASING_METRICS]:
            'Decreasing a bid should decrease cost and clicks',
        [BidFactoryError.CURRENT_BID_NOT_IN_SIMULATION]:
            'The current bid is not in the simulation points',
        [BidFactoryError.NO_POINT_CLOSE_TO_CPA]: 'No CPC bid close to the Target CPA',
        [BidFactoryError.NO_POINT_CLOSE_TO_ROAS]: 'No CPC bid close to the Target ROAS',
        [BidFactoryError.NO_POINT_WITH_MAX_PROFIT]: "Couldn't not find maximum profit cpc bid",
        [BidFactoryError.NO_VALID_SIMULATION_POINTS]:
            'No valid simulation points could be generated',
        [BidFactoryError.NO_CPC_BID_IN_SIMULATION]: 'No CPC Bid micros in simulation points',
    }

    export interface BidPrediction extends Regressions {
        simulation_points: RegressionMetrics[]
    }

    export interface RegressionMetrics {
        cost: number
        clicks: number
        cpc_bid: number
    }

    export interface RawAdGroupCriterionSimulation {
        ad_group_criterion: GadsFieldsRequired<
            resources.AdGroupCriterion,
            'resource_name' | 'effective_cpc_bid_micros'
        >

        ad_group_criterion_simulation: GadsFieldsRequired<
            resources.AdGroupCriterionSimulation,
            'resource_name' | 'cpc_bid_point_list' | 'start_date' | 'end_date'
        >
    }

    export enum BidAnalysis {
        LOW_CONVERSIONS = 'Low Conversions',
        LOW_CONVERSION_VALUE = 'Low Conv. Value',
        CURRENT = 'Current Bid',
        SUGGESTED = 'Suggested Bid',
        ABOVE_CAP = '> 30% Change',

        // CPA
        ABOVE_TARGET_CPA = 'Above Target CPA',
        ABOVE_AVERAGE_CPA = 'Above Avg. CPA',

        // ROAS
        BELOW_TARGET_ROAS = 'Below Target ROAS',
        BELOW_AVERAGE_ROAS = 'Below Avg. ROAS',
    }

    export interface SimulationPoint {
        clicks: number
        cost: number
        cpc_bid: number

        // CPA variables
        conversions: number
        cpa: number

        // ROAS variables
        conversions_value: number
        roas: number

        analysis: BidAnalysis

        is_best_bid?: boolean
        is_current_bid?: boolean
        is_bid_capped?: boolean
    }

    export type BestBid = BestBidWithoutPredictions | BestBidWithPredictions

    export interface BestBidWithoutPredictions {
        best_cpc_bid: number
        is_using_bid_factory: false
        valid: true
    }

    export interface BestBidWithPredictions {
        best_cpc_bid: number
        is_using_bid_factory: true
        simulation_points: SimulationPoint[]
        prediction_items: { x: number; y: number }[] // Used in the bid prediction chart
        valid: true
    }
    export interface InvalidBestBid {
        valid: false
    }

    export interface CpcBidPoint extends PredictionPointMetrics {
        cpc_bid: number
    }

    export interface CpcBidPointWithClicksAndConversions extends CpcBidPoint {
        clicks: number
        conversions: number
        value_per_conversion?: number
    }
}
